import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const WEB3FORMS_ACCESS_KEY = import.meta.env.VITE_WEB3FORMS_KEY;

const INTENT = {
  call: { consultationType: 'video', note: 'Set for a call. Change anything that does not fit.' },
  brief: { consultationType: 'written', note: 'Set for a written reply, with a scope and a number.' },
  pilot: { consultationType: 'written', investmentAmount: 'pilot-5k', note: 'The pilot, from $5,000, is selected.' },
  growth: { consultationType: 'video', investmentAmount: 'build-15k', note: 'The product build, from $15,000, is selected.' },
  enterprise: { consultationType: 'video', investmentAmount: 'longer', note: 'A longer engagement is selected.' },
};

const ConsultationForm = () => {
  const [params] = useSearchParams();
  const intent = INTENT[params.get('intent')] || null;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consultationType: intent?.consultationType || '',
    // NOTE: keep key name for compatibility with any existing handlers/integrations
    investmentAmount: intent?.investmentAmount || '',
    timeframe: '',
    interests: [],
    message: '',
    newsletter: false,
    terms: false
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Global-first options & Praesidio context
  const consultationTypes = [
    { value: 'video', label: 'Video call', description: 'Usually half an hour' },
    { value: 'phone', label: 'Phone call', description: 'The same conversation, by phone' },
    { value: 'in-person', label: 'In person', description: 'San Francisco or New York, by appointment' },
    { value: 'written', label: 'Written reply only', description: 'We send a scope and a number' }
  ];

  const budgetRanges = [
    { value: 'pilot-5k', label: 'Pilot, from $5,000' },
    { value: 'build-15k', label: 'Product build, from $15,000' },
    { value: 'longer', label: 'A longer engagement' },
    { value: 'prefer-not-to-say', label: 'I would rather discuss it' }
  ];

  const timeframes = [
    { value: 'immediate', label: 'Immediate (Within 30 days)' },
    { value: 'short-term', label: 'Short-term (1–3 months)' },
    { value: 'medium-term', label: 'Medium-term (3–6 months)' },
    { value: 'long-term', label: 'Long-term (6+ months)' },
    { value: 'exploring', label: 'Just exploring options' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!formData?.email?.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData?.email)) newErrors.email = 'Please enter a valid email';
    if (!formData?.phone?.trim()) newErrors.phone = 'Phone number is required';
    if (!formData?.consultationType) newErrors.consultationType = 'Please select a consultation type';
    if (!formData?.terms) newErrors.terms = 'You must agree to the terms and conditions';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors(prev => ({ ...prev, submit: '' }));

    // Map your field names into a Web3Forms payload
    const payload = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: 'New Consultation Request – Praesidio Care',
      from_name: `${formData.firstName} ${formData.lastName}`.trim(),
      replyto: formData.email,

      FirstName: formData.firstName,
      LastName: formData.lastName,
      Email: formData.email,
      Phone: formData.phone,
      ConsultationType: formData.consultationType,
      BudgetBand: formData.investmentAmount || 'Not specified',
      PreferredTimeframe: formData.timeframe || 'Not specified',
      Message: formData.message || '(none)',
      NewsletterOptIn: formData.newsletter ? 'Yes' : 'No',

      Page: typeof window !== 'undefined' ? window.location.href : '',
      Timestamp: new Date().toISOString(),

      // spam honeypot (must be empty for humans)
      botcheck: ''
    };

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(payload)
      });

      const data = await res.json();

      if (data?.success) {
        setIsSubmitted(true);
        // Optional: clear form state
        setFormData({
          firstName: '', lastName: '', email: '', phone: '', consultationType: '',
          investmentAmount: '', timeframe: '', interests: [], message: '',
          newsletter: false, terms: false
        });
      } else {
        setErrors(prev => ({
          ...prev,
          submit: 'Could not submit. Please try again or email contact@praesidiocare.com'
        }));
      }
    } catch (err) {
      setErrors(prev => ({
        ...prev,
        submit: 'Network error. Please try again.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="w-form p-6 md:p-8 text-center"
        role="status"
        aria-live="polite"
      >
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircle" size={32} className="text-success" />
        </div>
        <h3 className="text-2xl font-serif font-semibold text-foreground mb-4">
          Brief received
        </h3>
        <p className="text-muted-foreground mb-6 leading-relaxed">
          We will read it and reply from contact@praesidiocare.com. If it is faster,
          call +1 914 570 9780.
        </p>
        <div className="bg-muted/50 rounded-lg p-4 mb-6">
          <p className="text-sm text-muted-foreground">
            <strong>Next Steps:</strong> Look out for a confirmation email from contact@praesidiocare.com
            with available time slots and a short checklist to prepare for the discussion.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => {
            setIsSubmitted(false);
            setFormData({
              firstName: '', lastName: '', email: '', phone: '', consultationType: '',
              investmentAmount: '', timeframe: '', interests: [], message: '',
              newsletter: false, terms: false
            });
          }}
        >
          Send another brief
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.form
      onSubmit={handleSubmit}
      className="w-form p-6 md:p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.25 }}
      noValidate
      aria-live="polite"
    >
      {/* Invisible honeypot field (must remain empty) */}
      <input
        type="text"
        name="botcheck"
        tabIndex="-1"
        autoComplete="off"
        className="hidden"
        onChange={() => {}}
        value=""
        readOnly
        aria-hidden="true"
      />

      <div className="mb-6 md:mb-8">
        <h3 className="text-2xl font-serif font-semibold text-foreground mb-2">
          The brief
        </h3>
        <p className="text-muted-foreground">
          Who it is for, what it has to do, and roughly when you need it.
        </p>
        {intent?.note ? <p className="intent-note">{intent.note}</p> : null}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
        <Input
          label="First Name"
          type="text"
          placeholder="Enter your first name"
          value={formData?.firstName}
          onChange={(e) => handleInputChange('firstName', e?.target?.value)}
          error={errors?.firstName}
          autoComplete="given-name"
          inputMode="text"
          required
        />
        <Input
          label="Last Name"
          type="text"
          placeholder="Enter your last name"
          value={formData?.lastName}
          onChange={(e) => handleInputChange('lastName', e?.target?.value)}
          error={errors?.lastName}
          autoComplete="family-name"
          inputMode="text"
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
        <Input
          label="Email Address"
          type="email"
          placeholder="your.name@example.com"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          error={errors?.email}
          autoComplete="email"
          inputMode="email"
          required
        />
        <Input
          label="Phone Number"
          type="tel"
          placeholder="+1 914 570 9780"
          value={formData?.phone}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
          error={errors?.phone}
          autoComplete="tel"
          inputMode="tel"
          pattern="^[0-9+()\-.\s]{7,20}$"
          required
        />
      </div>

      <div className="mb-6">
        <Select
          label="Consultation Type"
          description="Choose your preferred consultation format"
          options={consultationTypes}
          value={formData?.consultationType}
          onChange={(value) => handleInputChange('consultationType', value)}
          error={errors?.consultationType}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
        <Select
          label="Project Budget (Indicative)"
          description="Approximate budget band for this engagement"
          options={budgetRanges}
          value={formData?.investmentAmount}
          onChange={(value) => handleInputChange('investmentAmount', value)}
          placeholder="Select range"
        />
        <Select
          label="Preferred Timeline"
          description="When would you like to start?"
          options={timeframes}
          value={formData?.timeframe}
          onChange={(value) => handleInputChange('timeframe', value)}
          placeholder="Select timeframe"
        />
      </div>

      <div className="mb-6">
        <Input
          label="Message (Optional)"
          type="text"
          placeholder="The people who will use it, and what must be true on the day it ships."
          value={formData?.message}
          onChange={(e) => handleInputChange('message', e?.target?.value)}
          className="min-h-[100px]"
          inputMode="text"
          autoComplete="off"
        />
      </div>

      <div className="space-y-4 mb-8">
        <Checkbox
          label="Email me when the lab publishes something about the work"
          checked={formData?.newsletter}
          onChange={(e) => handleInputChange('newsletter', e?.target?.checked)}
        />
        <Checkbox
          label="I agree to the Terms of Service and Privacy Policy"
          description="Required to schedule consultation"
          checked={formData?.terms}
          onChange={(e) => handleInputChange('terms', e?.target?.checked)}
          error={errors?.terms}
          required
        />
      </div>

      <Button
        type="submit"
        variant="default"
        fullWidth
        loading={isSubmitting}
        disabled={isSubmitting}
        aria-busy={isSubmitting}
        className="w-btn"
      >
        {isSubmitting ? 'Sending…' : 'Send the brief'}
      </Button>

      {/* Submit-level error (only shows on failure) */}
      {errors?.submit && (
        <p className="text-center text-sm text-destructive mt-3" role="alert">{errors.submit}</p>
      )}

      <p className="text-xs text-muted-foreground text-center mt-4">
        Sending this asks us to reply about this brief. San Francisco and New York.
      </p>
    </motion.form>
  );
};

export default ConsultationForm;
